import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Sparkles, Phone, Mail, MapPin } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { StatusBadge, StatusControl, StatusFilter } from "@/components/site/QuoteStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Qualcosa è andato storto.";
};

const Login = ({ onLogged }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { const { data } = await http.post("/auth/login", { email, password }); onLogged(data); }
    catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-32 max-w-sm rounded-[28px] border border-white/10 bg-[#0A1020] p-8" data-testid="admin-login-form">
      <LogoMark className="h-12 w-12" />
      <h1 className="mt-6 font-display text-3xl font-bold text-white">Area riservata</h1>
      <p className="mt-2 text-sm text-slate-400">Richieste di preventivo ricevute dal sito.</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-login-email" className="input-dark mt-6" />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-login-password" className="input-dark mt-3" />
      {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-cyan mt-6 w-full justify-center">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Entra</button>
    </form>
  );
};

const QuoteCard = ({ q, onSummary, onStatus }) => {
  const [busy, setBusy] = useState(false);
  const regen = async () => {
    setBusy(true);
    try { const { data } = await http.post(`/admin/quotes/${q.id}/summarize`); onSummary(q.id, data?.ai_summary ?? ""); }
    catch { onSummary(q.id, q.ai_summary); }
    finally { setBusy(false); }
  };
  return (
    <article className={`rounded-[24px] border bg-[#0A1020] p-6 transition-opacity duration-300 ${q.status === "closed" ? "border-white/5 opacity-70" : q.status === "new" ? "border-amber-400/30" : "border-white/10"}`} data-testid={`admin-quote-${q.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-display text-xl font-semibold text-white">{q.name}</h3>
            <StatusBadge status={q.status} id={q.id} />
          </div>
          <p className="mt-1 font-mono text-xs text-slate-400">{new Date(q.created_at).toLocaleString("it-IT")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(q.services ?? []).map((s) => <span key={s} className="rounded-full bg-[var(--cyan)]/15 px-3 py-1 text-xs font-semibold text-[var(--cyan)]">{s}</span>)}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Stato</p>
        <StatusControl id={q.id} status={q.status} onChange={onStatus} />
      </div>
      <div className="mt-4 rounded-2xl border border-[var(--cyan)]/30 bg-[var(--cyan)]/[0.05] p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--cyan)]"><Sparkles className="h-3.5 w-3.5" /> Riassunto AI</p>
          <button onClick={regen} disabled={busy} data-testid={`admin-summarize-${q.id}`} className="text-slate-400 hover:text-white" aria-label="Rigenera riassunto">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-200" data-testid={`admin-summary-${q.id}`}>{q.ai_summary || "Riassunto in preparazione… premi l'icona per generarlo."}</p>
      </div>
      <div className="mt-4 grid gap-2 text-sm text-slate-300 sm:grid-cols-3">
        <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="h-4 w-4 text-[var(--cyan)]" />{q.phone}</a>
        {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-2 break-all hover:text-white"><Mail className="h-4 w-4 text-[var(--cyan)]" />{q.email}</a>}
        <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[var(--cyan)]" />{q.zone}{q.property_type && ` · ${q.property_type}`}</span>
      </div>
      {q.message && <p className="mt-4 whitespace-pre-wrap border-t border-white/10 pt-4 text-sm text-slate-400">{q.message}</p>}
    </article>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await http.get("/admin/quotes"); setQuotes(Array.isArray(data) ? data : []); }
    catch { setQuotes([]); }
  }, []);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  useEffect(() => {
    if (user) load();
  }, [user, load]);
  const logout = async () => { await http.post("/auth/logout").catch(() => null); setUser(false); };
  const setSummary = (id, s) => setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, ai_summary: s } : q)));
  const [filter, setFilter] = useState("all");
  const setStatus = async (id, status) => {
    try {
      await http.patch(`/admin/quotes/${id}/status`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch (e) { toast.error(errText(e)); }
  };
  const counts = useMemo(() => quotes.reduce((acc, q) => ({ ...acc, [q.status]: (acc[q.status] ?? 0) + 1 }), { all: quotes.length }), [quotes]);
  const visible = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  if (user === null) return <div className="site grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[var(--cyan)]" /></div>;
  if (!user) return <div className="site min-h-screen px-4"><Login onLogged={setUser} /></div>;
  return (
    <div className="site min-h-screen px-4 py-10 sm:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3"><LogoMark /><h1 className="font-display text-2xl font-bold text-white">Richieste ricevute</h1></div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh" className="btn-ghost !px-4"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={logout} data-testid="admin-logout" className="btn-ghost !px-4"><LogOut className="h-4 w-4" /> Esci</button>
          </div>
        </div>
        <div className="mt-8"><StatusFilter value={filter} counts={counts} onChange={setFilter} /></div>
        <div className="mt-6 space-y-4" data-testid="admin-quote-list">
          {quotes.length === 0 && <p className="text-slate-400" data-testid="admin-empty">Nessuna richiesta per ora.</p>}
          {quotes.length > 0 && visible.length === 0 && <p className="text-slate-400" data-testid="admin-filter-empty">Nessuna richiesta in questo stato.</p>}
          {visible.map((q) => <QuoteCard key={q.id} q={q} onSummary={setSummary} onStatus={setStatus} />)}
        </div>
      </div>
    </div>
  );
}
